import React, { useContext, useState } from 'react';
import AttributeListPill from './AttributeListPill';
import ReactDOM from 'react-dom';
import { ProductsContext } from '../ProductsContext';
import { TFilters } from 'types/hooks/useTable';
import ViewMoreButton from 'Components/Common/components/ViewMoreButton';
import Button from 'Components/DesignSystem/Button/Button';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';

type MyProps = {
  handleAttributesChange: (attributes: string[]) => void,
  filters: TFilters
}

/* Inline copy of assests/img/filter-icon-img.svg — the funnel keeps its accent-blue
   identity via tw-text-accent and now themes; the <img> could not take currentColor. */
const FilterFunnelIcon = () => (
  <svg className="tw-w-[16.56px] tw-h-[15px] tw-text-accent" width="22" height="20" viewBox="0 0 22 20" fill="none" aria-hidden="true">
    <path d="M21 1.48242H1L9 10.9424V17.4824L13 19.4824V10.9424L21 1.48242Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The Filter drawer, on the DS since § 67: `Button subtle` trigger and the DS
 * `SidePanel` (role="dialog", aria-modal, Escape + backdrop dismissal, focus
 * handling) in place of a hand-rolled portal drawer whose overlay class was the
 * misspelling `tw-abosulte` and whose close button's accessible name was the
 * literal character "X".
 *
 * Functionality is deliberately unchanged: the same portal host requirement
 * (`#sidepanel-portal` — nothing renders without it), the same toggle semantics
 * (categorySearch resets on the OPEN transition only), the same error/list/
 * view-more body. Escape-to-close is the one addition, via SidePanel.
 */
const ProductFilter = ({ filters, handleAttributesChange }: MyProps) => {
  const prodContext = useContext<any>(ProductsContext);

  const { attributes, attributeError, totalAttributeCount, attributePage, setAttributePage } = prodContext

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    if (!isMenuOpen) {
      prodContext.setCategorySearch("")
    }
    setIsMenuOpen(!isMenuOpen);
  };
  const onClickedViewMoreButton = () => {
    setAttributePage(attributePage + 1)
  };

  const sidePanelPortal = document.querySelector('#sidepanel-portal')

  return (
    <div>
      <div data-testid="productlist-productfilter-div-1" className="tw-mx-3">
        <Button
          variant="subtle"
          id="products-filter-btn"
          data-testid="products-filter-btn"
          aria-haspopup="dialog"
          aria-expanded={isMenuOpen}
          showIcon
          icon={<FilterFunnelIcon />}
          onClick={toggleMenu}
        >
          Filter
        </Button>
      </div>
      {(isMenuOpen && sidePanelPortal) &&
        ReactDOM.createPortal(
          <div id="products-filter-panel">
            <SidePanel
              isOpen
              onClose={toggleMenu}
              title="Filter"
              tabs={[]}
              data-testid="products-filter-panel"
              headerActions={
                <Button
                  variant="subtle"
                  size="sm"
                  id="products-filter-panel-btn-close"
                  data-testid="productlist-productfilter-button-1"
                  aria-label="Close filter panel"
                  onClick={toggleMenu}
                >
                  <Cross size={16} aria-hidden="true" />
                </Button>
              }
            >
              {attributeError && <span>Error loading Attributes</span>}

              {!attributeError && <AttributeListPill filters={filters} attributes={attributes} handleChange={handleAttributesChange} />}
              {/* While a further page of attributes is in flight the button's slot shows the
                  DS Spinner instead of going empty (the button's own condition already hid it).
                  Guarded on attributes already present so the drawer's INITIAL load is unchanged. */}
              {prodContext.loadingAttributes && attributes?.length > 0
                ? <Spinner size="sm" label="Loading more attributes" className="tw-my-2" data-testid="products-filter-attributes-loading" />
                : <ViewMoreButton condition={
                    attributes?.length < totalAttributeCount && !prodContext.loadingAttributes
                  } handleClick={onClickedViewMoreButton} state={false} />}
            </SidePanel>
          </div>
          , sidePanelPortal)}
    </div>
  );
};

export default ProductFilter;
